'use client';

import Link from 'next/link';
import { BRAND } from "@/lib/brand";
import { useAuth } from '@/contexts/AuthContext';
import { Shield, ArrowLeft, LogOut, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function NotFound() {
  const { user, signOut, loading } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header - only show when logged in */}
      {user && !loading && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <Link
            href="/"
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {BRAND.name}
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-gray-500">Logged in as</p>
              <p className="text-sm font-medium text-gray-900">{user.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-gray-400" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Page not found
          </h1>

          <p className="text-gray-600 mb-8">
            The page you're looking for doesn't exist or has been moved.
          </p>

          {loading ? (
            <div className="h-10" />
          ) : user ? (
            // Logged in state
            <div className="space-y-3">
              <Link href="/orgs/select">
                <Button className="w-full">
                  <Building2 className="h-4 w-4 mr-2" />
                  Go to your workspaces
                </Button>
              </Link>
              <Link
                href="/"
                className="block text-sm text-gray-500 hover:text-gray-700"
              >
                Or return to homepage
              </Link>
            </div>
          ) : (
            // Logged out state
            <div className="space-y-3">
              <Link href="/">
                <Button className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to homepage
                </Button>
              </Link>
              <p className="text-sm text-gray-500">
                Have an account?{' '}
                <Link
                  href="/auth/signin"
                  className="text-gray-900 hover:underline font-medium"
                >
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
